import { getCollection } from './firestoreService';
import { getAnalyticsEvents } from './analyticsService';
import { getAllSubscriptions } from './subscriptionService';
import { format, startOfDay, startOfWeek, startOfMonth, subDays, differenceInDays } from 'date-fns';

export interface TrendDataPoint {
  date: string;
  value: number;
}

export interface TrendsAnalytics {
  activeUsers: {
    daily: TrendDataPoint[];
    weekly: TrendDataPoint[];
    monthly: TrendDataPoint[];
  };
  revenue: TrendDataPoint[];
  subscriptions: TrendDataPoint[];
  engagement: TrendDataPoint[];
  featureAdoption: TrendDataPoint[];
  growthRates: {
    mom: number;
    yoy: number;
    compound: number;
  };
}

export const getTrendsAnalytics = async (
  days: number = 90
): Promise<TrendsAnalytics> => {
  try {
    const events = await getAnalyticsEvents(days * 2);
    const subscriptions = await getAllSubscriptions();
    const rawUsers = await getCollection('users', undefined, undefined, undefined, 10000);
    const users = rawUsers.filter((u: any) => !u.deletedAt && !u.isDeleted);

    const now = new Date();
    const startDate = subDays(now, days);

    // Daily active users
    const dailyActiveUsers = new Map<string, Set<string>>();
    events.forEach((event) => {
      if (event.eventName === 'session_start' || event.eventName === 'app_launch') {
        const dateKey = format(startOfDay(event.timestamp), 'yyyy-MM-dd');
        if (!dailyActiveUsers.has(dateKey)) {
          dailyActiveUsers.set(dateKey, new Set());
        }
        dailyActiveUsers.get(dateKey)!.add(event.userId);
      }
    });

    const dailyActive: TrendDataPoint[] = Array.from(dailyActiveUsers.entries())
      .map(([date, userIds]) => ({ date, value: userIds.size }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Weekly active users
    const weeklyActiveUsers = new Map<string, Set<string>>();
    events.forEach((event) => {
      if (event.eventName === 'session_start' || event.eventName === 'app_launch') {
        const dateKey = format(startOfWeek(event.timestamp, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        if (!weeklyActiveUsers.has(dateKey)) {
          weeklyActiveUsers.set(dateKey, new Set());
        }
        weeklyActiveUsers.get(dateKey)!.add(event.userId);
      }
    });

    const weeklyActive: TrendDataPoint[] = Array.from(weeklyActiveUsers.entries())
      .map(([date, userIds]) => ({ date, value: userIds.size }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Monthly active users
    const monthlyActiveUsers = new Map<string, Set<string>>();
    events.forEach((event) => {
      if (event.eventName === 'session_start' || event.eventName === 'app_launch') {
        const dateKey = format(startOfMonth(event.timestamp), 'yyyy-MM');
        if (!monthlyActiveUsers.has(dateKey)) {
          monthlyActiveUsers.set(dateKey, new Set());
        }
        monthlyActiveUsers.get(dateKey)!.add(event.userId);
      }
    });

    const monthlyActive: TrendDataPoint[] = Array.from(monthlyActiveUsers.entries())
      .map(([date, userIds]) => ({ date, value: userIds.size }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Revenue trends (monthly)
    const revenueByMonth = new Map<string, number>();
    subscriptions.forEach((sub: any) => {
      if (sub.status === 'active' && !sub.isTrial && sub.createdAt) {
        const created = sub.createdAt instanceof Date ? sub.createdAt : new Date(sub.createdAt);
        const monthKey = format(startOfMonth(created), 'yyyy-MM');
        revenueByMonth.set(monthKey, (revenueByMonth.get(monthKey) || 0) + (sub.amount || 0));
      }
    });

    const revenue: TrendDataPoint[] = Array.from(revenueByMonth.entries())
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Subscription trends
    const subscriptionsByMonth = new Map<string, number>();
    subscriptions.forEach((sub: any) => {
      if (sub.createdAt) {
        const created = sub.createdAt instanceof Date ? sub.createdAt : new Date(sub.createdAt);
        const monthKey = format(startOfMonth(created), 'yyyy-MM');
        subscriptionsByMonth.set(monthKey, (subscriptionsByMonth.get(monthKey) || 0) + 1);
      }
    });

    const subscriptionTrends: TrendDataPoint[] = Array.from(subscriptionsByMonth.entries())
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Engagement trends (events per day)
    const eventsByDate = new Map<string, number>();
    events.forEach((event) => {
      const dateKey = format(startOfDay(event.timestamp), 'yyyy-MM-dd');
      eventsByDate.set(dateKey, (eventsByDate.get(dateKey) || 0) + 1);
    });

    const engagement: TrendDataPoint[] = Array.from(eventsByDate.entries())
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Feature adoption trends (assistant usage over time)
    const assistantUsageByDate = new Map<string, number>();
    events.forEach((event) => {
      if (event.eventName.includes('assistant')) {
        const dateKey = format(startOfDay(event.timestamp), 'yyyy-MM-dd');
        assistantUsageByDate.set(dateKey, (assistantUsageByDate.get(dateKey) || 0) + 1);
      }
    });

    const featureAdoption: TrendDataPoint[] = Array.from(assistantUsageByDate.entries())
      .map(([date, value]) => ({ date, value }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Growth rates
    const currentMonth = monthlyActive[monthlyActive.length - 1]?.value || 0;
    const previousMonth = monthlyActive.length > 1 ? monthlyActive[monthlyActive.length - 2]?.value || 0 : 0;
    const mom = previousMonth > 0 ? ((currentMonth - previousMonth) / previousMonth) * 100 : 0;

    const oneYearAgo = monthlyActive.length > 12 ? monthlyActive[monthlyActive.length - 13]?.value || 0 : 0;
    const yoy = oneYearAgo > 0 ? ((currentMonth - oneYearAgo) / oneYearAgo) * 100 : 0;

    // Compound growth (simplified)
    const firstMonth = monthlyActive[0]?.value || 1;
    const compound = monthlyActive.length > 1
      ? (Math.pow(currentMonth / firstMonth, 1 / (monthlyActive.length - 1)) - 1) * 100
      : 0;

    return {
      activeUsers: {
        daily: dailyActive,
        weekly: weeklyActive,
        monthly: monthlyActive,
      },
      revenue,
      subscriptions: subscriptionTrends,
      engagement,
      featureAdoption,
      growthRates: {
        mom,
        yoy,
        compound,
      },
    };
  } catch (error) {
    console.error('Error calculating trends analytics:', error);
    throw error;
  }
};

